import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

/**
 * The Feather Reel's persistent "ask your server" panel — pinned below the story.
 * Ported from iOS `PersistentAskPanel` (`ReelSubviews.swift`). Presentational only: the overlay
 * resolves the localized title/subtitle and passes them in. Colours mirror `reel-theme.ts`.
 */
@Component({
    selector: 'app-reel-ask-panel',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div class="ask-divider"></div>
    <div class="ask-wrap">
      <div class="ask-card">
        <span *ngIf="hasPrice" class="ask-price">
          <span class="ask-price-value">{{ price }}</span>
          <span class="ask-price-sep"> · </span>
          <span class="ask-price-label">{{ 'reel.onYourBill' | translate }}</span>
        </span>

        <div class="ask-row">
          <div class="ask-text">
            <div class="ask-title">{{ title }}</div>
            <div class="ask-subtitle-row">
              <!-- Coral service bell (WaiterIcon parity) -->
              <svg class="ask-bell" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3.2a1.15 1.15 0 0 1 1.15 1.15v.62a6.2 6.2 0 0 1 5.05 6.09v3.29l1.35 1.98c.5.73-.02 1.72-.9 1.72H5.35c-.88 0-1.4-.99-.9-1.72l1.35-1.98V11.06A6.2 6.2 0 0 1 10.85 4.97v-.62A1.15 1.15 0 0 1 12 3.2Z" fill="currentColor"/>
                <path d="M9.6 18.2h4.8a2.4 2.4 0 0 1-4.8 0Z" fill="currentColor"/>
              </svg>
              <span class="ask-subtitle">{{ subtitle }}</span>
            </div>
          </div>

          <img *ngIf="imageUrl" class="ask-image" [src]="imageUrl" alt="" />
        </div>
      </div>
    </div>
  `,
    styles: [`
    :host { display: block; background: #141414; }
    .ask-divider { height: 1px; background: #202020; }
    .ask-wrap {
      padding: 22px 16px calc(16px + env(safe-area-inset-bottom, 0px));
    }
    .ask-card {
      position: relative;
      background: linear-gradient(180deg, #1E1E1E 0%, #191919 100%);
      border: 1px solid #2B2B2B;
      border-radius: 18px;
      padding: 24px 16px 16px;
    }
    .ask-price {
      position: absolute;
      top: 0;
      left: 18px;
      transform: translateY(-50%);
      display: inline-flex;
      align-items: baseline;
      background: #F26B6B;
      color: #141414;
      border-radius: 9px;
      padding: 5px 14px;
      box-shadow: 0 6px 9px rgba(242, 107, 107, 0.3);
      white-space: nowrap;
    }
    .ask-price-value { font-size: 15px; font-weight: 800; }
    .ask-price-sep, .ask-price-label { font-size: 15px; font-weight: 500; opacity: 0.85; }
    .ask-row { display: flex; align-items: flex-start; gap: 12px; }
    .ask-text { flex: 1 1 auto; min-width: 0; }
    .ask-title {
      color: #FFFFFF;
      font-size: 19px;
      font-weight: 700;
      line-height: 1.2;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .ask-subtitle-row { display: flex; align-items: flex-start; gap: 8px; margin-top: 5px; }
    .ask-bell { width: 19px; height: 19px; flex: 0 0 auto; color: #F26B6B; margin-top: 1px; }
    .ask-subtitle {
      color: #9A9A9A;
      font-size: 15px;
      font-weight: 500;
      line-height: 1.25;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .ask-image {
      width: 82px;
      height: 54px;
      flex: 0 0 auto;
      object-fit: contain;
      border-radius: 8px;
    }
  `],
    standalone: false
})
export class ReelAskPanelComponent {
  @Input() title = '';
  @Input() subtitle = '';
  @Input() imageUrl: string | null = null;
  @Input() price: string | null = null;

  get hasPrice(): boolean {
    return !!this.price && this.price.trim().length > 0;
  }
}
